import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { catchError, Observable, of, Subject, takeUntil, tap, throwError } from 'rxjs';
import { ConfigService } from './core/config.service';
import { EmbeddedTokenResponse } from '../interfaces/embedded-token-model';
import * as pbi from 'powerbi-client';
import { CachedConfigEntry } from '../interfaces/power-bi/CachedConfig';
import { CachedTokenEntry } from '../interfaces/power-bi/CachedToken';

@Injectable({ providedIn: 'root' })
export class PowerbiReportService implements OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  private readonly _dispatchToken = new Subject<EmbeddedTokenResponse>();
  private readonly tokenCache = new Map<string, CachedTokenEntry>();
  private readonly configCache = new Map<string, CachedConfigEntry>();
  private readonly tokenTTL = 30 * 60 * 1000;
  private readonly configTTL = 30 * 60 * 1000;

  constructor(private readonly configService: ConfigService, private readonly http: HttpClient) {}

  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Generates a new embedded token for the specified type, or returns a cached token if still valid.
   *
   * @param {string} type - The type of token to generate (used as cache key).
   * @returns {Observable<EmbeddedTokenResponse>} Observable that emits the embedded token response.
   *
   * The token is cached for a limited time (tokenTTL). If a valid cached token exists, it is returned immediately.
   * Otherwise, an HTTP request is made to fetch a new token from the backend.
   *
   * The observable completes when the component/service is destroyed.
   * Errors from the HTTP request are propagated through the observable.
   */
  public generateToken(type: string): Observable<EmbeddedTokenResponse> {
    const cached = this.tokenCache.get(type);
    const now = Date.now();

    if (cached && now - cached.timestamp < this.tokenTTL) {
      return of(cached.token);
    }

    return this.http
      .get<EmbeddedTokenResponse>(
        `${this.configService.config.backendUri}/PowerBi/generateToken?type=${type}`,
      )
      .pipe(
        tap(token => {
          this.tokenCache.set(type, { token, timestamp: now });
        }),
        takeUntil(this._destroying$),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  /**
   * Returns an observable to listen for dispatched embedded tokens.
   *
   * @returns {Observable<EmbeddedTokenResponse>} Observable emitting embedded tokens.
   */
  getDispatchTokenObservable() {
    return this._dispatchToken.asObservable();
  }

  /**
   * Emits a new embedded token to subscribers.
   *
   * @param {EmbeddedTokenResponse} token - The embedded token to dispatch.
   */
  sendToken(token: EmbeddedTokenResponse) {
    this._dispatchToken.next(token);
  }

  /**
   * Removes the cached token for the specified type, forcing a refresh on next request.
   *
   * @param {string} type - The token type to invalidate from the cache.
   */
  invalidateToken(type: string) {
    this.tokenCache.delete(type);
  }

  /**
   * Caches a Power BI embed configuration along with the current timestamp.
   *
   * This allows later retrieval with TTL validation to ensure the config is fresh.
   *
   * @param {string} token - The cache key (typically the embed token).
   * @param {pbi.IEmbedConfiguration} config - The embed configuration to cache.
   */
  cacheConfig(token: string, config: pbi.IEmbedConfiguration) {
    this.configCache.set(token, { config, timestamp: Date.now() });
  }

  /**
   * Retrieves a cached Power BI embed configuration if it is still valid (not expired).
   *
   * The configuration is considered valid if it exists in the cache and its age is less than the configured TTL.
   * If the configuration is expired, it will be removed from the cache and `undefined` will be returned.
   *
   * @param {string} token - The cache key (typically the embed token).
   * @returns {pbi.IEmbedConfiguration | undefined} - The cached configuration, or `undefined` if not found or expired.
   */
  getCachedConfig(token: string): pbi.IEmbedConfiguration | undefined {
    const entry = this.configCache.get(token);
    const now = Date.now();

    if (entry && now - entry.timestamp < this.configTTL) {
      return entry.config;
    }

    this.configCache.delete(token);
    return undefined;
  }
}
